import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tablet, CheckCircle2, ShieldCheck, LogOut, FileText, Euro, FileSignature } from "lucide-react";
import LegalSignaturePad from "@/components/LegalSignaturePad";
import AdultContractPanel from "@/components/AdultContractPanel";
import DataConsentPanel from "@/components/DataConsentPanel";
import SepaPanel from "@/components/SepaPanel";

const backend = process.env.REACT_APP_BACKEND_URL || "";
const client = axios.create({ baseURL: `${backend}/api`, timeout: 20000 });

const PM_LABEL = {
    domiciliacion: "Domiciliación bancaria",
    tarjeta: "Tarjeta",
    efectivo: "Efectivo",
    transferencia: "Transferencia",
};

export default function KioskMode() {
    const [step, setStep] = useState("login");
    const [form, setForm] = useState({ dni: "", birth_date: "" });
    const [token, setToken] = useState(null);
    const [patient, setPatient] = useState(null);
    const [pending, setPending] = useState([]);
    const [pendingQuotes, setPendingQuotes] = useState([]);
    const [templates, setTemplates] = useState({});
    const [signOpen, setSignOpen] = useState(null);          // legal doc
    const [signQuoteOpen, setSignQuoteOpen] = useState(null); // quote object
    const [submitting, setSubmitting] = useState(false);
    const [busy, setBusy] = useState(false);
    const [contractPending, setContractPending] = useState(false);
    const [contractOpen, setContractOpen] = useState(false);
    const [consentPending, setConsentPending] = useState(false);
    const [consentOpen, setConsentOpen] = useState(false);
    const [sepaPending, setSepaPending] = useState(false);
    const [sepaOpen, setSepaOpen] = useState(false);

    useEffect(() => {
        const url = new URL(window.location.href);
        const dni = url.searchParams.get("patient") || "";
        const dob = url.searchParams.get("dob") || "";
        if (dni || dob) setForm({ dni, birth_date: dob });
    }, []);

    const start = async () => {
        if (!form.dni.trim() || !form.birth_date) return toast.error("Introduce DNI y fecha de nacimiento");
        setBusy(true);
        try {
            const { data } = await client.post("/kiosk/start", form);
            setToken(data.token);
            setPatient(data.patient);
            setPending(data.pending_documents || []);
            setPendingQuotes(data.pending_quotes || []);
            setContractPending(!!data.adult_contract_pending);
            setConsentPending(!!data.data_consent_pending);
            setSepaPending(!!data.sepa_pending);
            const tpl = await client.get("/legal-templates", {
                headers: { Authorization: `Bearer ${data.token}` },
            }).catch(() => ({ data: {} }));
            setTemplates(tpl.data);
            setStep("documents");
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo iniciar el quiosco");
        } finally { setBusy(false); }
    };

    const doSign = async (dataUrl) => {
        if (!signOpen) return;
        setSubmitting(true);
        try {
            await client.post("/kiosk/sign", {
                doc_type: signOpen, signature_data_url: dataUrl,
            }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success("Documento firmado correctamente");
            setSignOpen(null);
            setPending((p) => p.filter((x) => x.doc_type !== signOpen));
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error");
        } finally { setSubmitting(false); }
    };

    const doSignQuote = async (dataUrl) => {
        if (!signQuoteOpen) return;
        setSubmitting(true);
        try {
            const r = await client.post("/kiosk/quotes/sign", {
                quote_id: signQuoteOpen.id, signature_data_url: dataUrl,
            }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success(`Presupuesto ${r.data.quote_number} aceptado y firmado`);
            setPendingQuotes((qs) => qs.filter((q) => q.id !== signQuoteOpen.id));
            setSignQuoteOpen(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error");
        } finally { setSubmitting(false); }
    };

    const exit = () => {
        setStep("login"); setToken(null); setPatient(null);
        setPending([]); setPendingQuotes([]);
        setContractPending(false); setContractOpen(false);
        setConsentPending(false); setConsentOpen(false);
        setSepaPending(false); setSepaOpen(false);
        setForm({ dni: "", birth_date: "" });
    };

    const quoteContent = signQuoteOpen ? renderQuoteHtml(signQuoteOpen) : "";

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="kiosk-page">
            <div className={`w-full ${contractOpen ? "max-w-6xl" : "max-w-3xl"}`}>
                <div className="flex items-center gap-3 mb-6 justify-center">
                    <div className="w-11 h-11 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-lg">B</div>
                    <div>
                        <div className="font-semibold tracking-tight text-lg">EN Boreal</div>
                        <div className="text-xs uppercase-label text-primary flex items-center gap-1"><Tablet size={12} /> Modo Quiosco — firma digital</div>
                    </div>
                </div>

                {step === "login" && (
                    <Card className="p-6" data-testid="kiosk-login-card">
                        <div className="text-lg font-semibold mb-1">Identifícate para firmar tus documentos</div>
                        <p className="text-sm text-muted-foreground mb-5">
                            Introduce tu DNI/NIF y tu fecha de nacimiento. Solo verás tus documentos y presupuestos pendientes.
                        </p>
                        <div className="space-y-3">
                            <div>
                                <Label className="uppercase-label mb-1 block">DNI/NIF</Label>
                                <Input value={form.dni} onChange={(e) => setForm({ ...form, dni: e.target.value })} placeholder="12345678A" data-testid="kiosk-dni" />
                            </div>
                            <div>
                                <Label className="uppercase-label mb-1 block">Fecha de nacimiento</Label>
                                <Input type="date" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} data-testid="kiosk-dob" />
                            </div>
                            <Button className="w-full btn-cta" onClick={start} disabled={busy} data-testid="kiosk-start">
                                <ShieldCheck size={16} className="mr-1" /> {busy ? "Verificando…" : "Continuar"}
                            </Button>
                        </div>
                    </Card>
                )}

                {step === "documents" && patient && (
                    <Card className="p-6" data-testid="kiosk-documents-card">
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <div className="uppercase-label mb-1">Hola,</div>
                                <div className="text-xl font-semibold">{patient.name}</div>
                            </div>
                            <Button variant="ghost" size="sm" onClick={exit} data-testid="kiosk-exit">
                                <LogOut size={14} className="mr-1" /> Salir
                            </Button>
                        </div>

                        {pending.length === 0 && pendingQuotes.length === 0 && !contractPending && !consentPending && !consentOpen && !sepaPending && !sepaOpen ? (
                            <div className="rounded-md border border-primary/30 bg-primary/5 p-6 text-center" data-testid="kiosk-all-done">
                                <CheckCircle2 size={32} className="text-primary mx-auto mb-2" />
                                <div className="font-semibold text-primary">¡Todo está firmado!</div>
                                <div className="text-xs text-muted-foreground mt-1">Puedes devolver la tablet al personal.</div>
                                <Button className="mt-4" variant="outline" onClick={exit}>Terminar sesión</Button>
                            </div>
                        ) : (
                            <>
                                {contractPending && !contractOpen && (
                                    <div className="rounded-md border border-primary/40 bg-primary/5 p-4 flex items-center justify-between gap-3 mb-4" data-testid="kiosk-contract-cta">
                                        <div>
                                            <div className="uppercase-label text-primary flex items-center gap-1">
                                                <FileSignature size={12} /> Contrato de servicios adultos
                                            </div>
                                            <div className="font-semibold mt-1">Rellena y firma tu contrato</div>
                                            <div className="text-xs text-muted-foreground">Revisa los datos y firma directamente en esta tablet.</div>
                                        </div>
                                        <Button className="btn-cta" onClick={() => setContractOpen(true)} data-testid="kiosk-open-contract">
                                            Abrir contrato
                                        </Button>
                                    </div>
                                )}
                                {contractOpen && (
                                    <div className="mb-4 space-y-2" data-testid="kiosk-contract-form">
                                        <div className="flex items-center justify-between">
                                            <div className="uppercase-label">Contrato de servicios adultos</div>
                                            <Button variant="ghost" size="sm" onClick={() => setContractOpen(false)} data-testid="kiosk-close-contract">Volver</Button>
                                        </div>
                                        <AdultContractPanel
                                            patientId={patient?.id}
                                            kiosk
                                            apiClient={client}
                                            authHeaders={{ Authorization: `Bearer ${token}` }}
                                            onSigned={() => {
                                                setContractPending(false);
                                                toast.success("Contrato firmado. Ya puedes descargarlo, imprimirlo o enviarlo por email.");
                                                client.get("/kiosk/sepa", { headers: { Authorization: `Bearer ${token}` } })
                                                    .then(({ data }) => setSepaPending(!!data.required && data.status !== "signed")).catch(() => {});
                                            }}
                                        />
                                    </div>
                                )}
                                {consentPending && !consentOpen && (
                                    <div className="rounded-md border border-primary/40 bg-primary/5 p-4 flex items-center justify-between gap-3 mb-4" data-testid="kiosk-data-consent-cta">
                                        <div>
                                            <div className="uppercase-label text-primary flex items-center gap-1">
                                                <FileSignature size={12} /> Protección de datos
                                            </div>
                                            <div className="font-semibold mt-1">Lee, completa y firma el consentimiento</div>
                                            <div className="text-xs text-muted-foreground">Tus datos ya están rellenados; revísalos y firma en esta tablet.</div>
                                        </div>
                                        <Button className="btn-cta" onClick={() => setConsentOpen(true)} data-testid="kiosk-open-data-consent">
                                            Abrir documento
                                        </Button>
                                    </div>
                                )}
                                {consentOpen && (
                                    <div className="mb-4 space-y-2" data-testid="kiosk-data-consent-form">
                                        <div className="flex items-center justify-between">
                                            <div className="uppercase-label">Protección de datos</div>
                                            <Button variant="ghost" size="sm" onClick={() => setConsentOpen(false)} data-testid="kiosk-close-data-consent">Volver</Button>
                                        </div>
                                        <DataConsentPanel
                                            patientId={patient?.id}
                                            kiosk
                                            apiClient={client}
                                            authHeaders={{ Authorization: `Bearer ${token}` }}
                                            onSigned={() => { setConsentPending(false); if (sepaPending) { setSepaOpen(true); } }}
                                        />
                                    </div>
                                )}
                                {sepaPending && !sepaOpen && !consentPending && (
                                    <div className="rounded-md border border-primary/40 bg-primary/5 p-4 flex items-center justify-between gap-3 mb-4" data-testid="kiosk-sepa-cta">
                                        <div>
                                            <div className="uppercase-label text-primary flex items-center gap-1">
                                                <FileSignature size={12} /> Orden SEPA
                                            </div>
                                            <div className="font-semibold mt-1">Completa y firma la domiciliación bancaria</div>
                                            <div className="text-xs text-muted-foreground">Has elegido pago por domiciliación en tu contrato.</div>
                                        </div>
                                        <Button className="btn-cta" onClick={() => setSepaOpen(true)} data-testid="kiosk-open-sepa">Abrir documento</Button>
                                    </div>
                                )}
                                {sepaOpen && (
                                    <div className="mb-4 space-y-2" data-testid="kiosk-sepa-form">
                                        <div className="flex items-center justify-between">
                                            <div className="uppercase-label">Orden SEPA</div>
                                            <Button variant="ghost" size="sm" onClick={() => setSepaOpen(false)} data-testid="kiosk-close-sepa">Volver</Button>
                                        </div>
                                        <SepaPanel kiosk apiClient={client} authHeaders={{ Authorization: `Bearer ${token}` }}
                                            onSigned={() => setSepaPending(false)} />
                                    </div>
                                )}
                                {pending.length > 0 && (
                                    <>
                                        <div className="uppercase-label mb-2">Documentos legales pendientes</div>
                                        <div className="space-y-3 mb-6">
                                            {pending.map((d) => (
                                                <div key={d.doc_type} className="rounded-md border border-border p-4 flex items-center justify-between gap-3" data-testid={`kiosk-doc-${d.doc_type}`}>
                                                    <div>
                                                        <div className="font-medium">{d.label}</div>
                                                        <Badge variant="outline" className="text-red-700 border-red-300 text-[10px] mt-1">Pendiente de firma</Badge>
                                                    </div>
                                                    <Button className="btn-cta" onClick={() => setSignOpen(d.doc_type)} data-testid={`kiosk-sign-${d.doc_type}`}>
                                                        Leer y firmar
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}

                                {pendingQuotes.length > 0 && (
                                    <>
                                        <div className="uppercase-label mb-2 flex items-center gap-1.5">
                                            <FileText size={12} /> Presupuestos pendientes de aceptar
                                        </div>
                                        <div className="space-y-3">
                                            {pendingQuotes.map((q) => (
                                                <div key={q.id} className="rounded-md border border-border p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`kiosk-quote-${q.id}`}>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <div className="font-medium font-mono text-sm">{q.quote_number}</div>
                                                            <Badge variant="outline" className="text-amber-700 border-amber-300 text-[10px]">Pendiente</Badge>
                                                        </div>
                                                        <div className="text-sm text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                                                            <span className="inline-flex items-center gap-1 font-mono text-foreground"><Euro size={12} /> {(q.total || 0).toFixed(2)} €</span>
                                                            {q.valid_until && <span className="text-xs">· Válido hasta {q.valid_until.slice(0, 10)}</span>}
                                                            {q.payment_method && <span className="text-xs">· {PM_LABEL[q.payment_method] || q.payment_method}</span>}
                                                        </div>
                                                        {q.items?.length > 0 && (
                                                            <div className="text-xs text-muted-foreground mt-1 truncate max-w-md">
                                                                {q.items[0].description}{q.items.length > 1 ? ` +${q.items.length - 1} más` : ""}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <Button className="btn-cta" onClick={() => setSignQuoteOpen(q)} data-testid={`kiosk-sign-quote-${q.id}`}>
                                                        Revisar y firmar
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </Card>
                )}
            </div>

            <LegalSignaturePad
                open={!!signOpen}
                onOpenChange={(v) => !v && setSignOpen(null)}
                kiosk
                title={signOpen ? (templates[signOpen]?.label || "Documento") : ""}
                content={signOpen ? (templates[signOpen]?.content || "") : ""}
                onSign={doSign}
                submitting={submitting}
            />

            <LegalSignaturePad
                open={!!signQuoteOpen}
                onOpenChange={(v) => !v && setSignQuoteOpen(null)}
                kiosk
                title={signQuoteOpen ? `Presupuesto ${signQuoteOpen.quote_number}` : ""}
                content={quoteContent}
                onSign={doSignQuote}
                submitting={submitting}
            />
        </div>
    );
}

function renderQuoteHtml(q) {
    if (!q) return "";
    const items = (q.items || []).map((it) => `
        <tr>
            <td style="padding:6px;border-bottom:1px solid #E5E7EB">${(it.description || "").replace(/</g, "&lt;")}</td>
            <td style="padding:6px;text-align:right;border-bottom:1px solid #E5E7EB">${it.qty}</td>
            <td style="padding:6px;text-align:right;border-bottom:1px solid #E5E7EB font-family:monospace">${(it.price || 0).toFixed(2)} €</td>
            <td style="padding:6px;text-align:right;border-bottom:1px solid #E5E7EB font-family:monospace">${((it.qty || 0) * (it.price || 0)).toFixed(2)} €</td>
        </tr>`).join("");
    const pmLabel = q.payment_method ? (PM_LABEL[q.payment_method] || q.payment_method) : "por definir";
    const validUntil = q.valid_until ? q.valid_until.slice(0, 10) : "—";
    return `
        <div style="font-family:system-ui, sans-serif; color:#2E353B;">
            <h3 style="color:#5AC29A;margin-bottom:4px">Presupuesto ${q.quote_number}</h3>
            <p style="color:#B0574F;font-size:12px;margin-top:0"><strong>DOCUMENTO NO VÁLIDO COMO FACTURA</strong> — sujeto a aceptación</p>
            <p style="font-size:13px">Válido hasta: <strong>${validUntil}</strong> · Forma de pago prevista: <strong>${pmLabel}</strong></p>
            <table style="width:100%;border-collapse:collapse;font-size:13px;margin-top:8px">
                <thead><tr style="background:#5AC29A;color:white">
                    <th style="padding:6px;text-align:left">Descripción</th>
                    <th style="padding:6px;text-align:right">Cant.</th>
                    <th style="padding:6px;text-align:right">Precio</th>
                    <th style="padding:6px;text-align:right">Importe</th>
                </tr></thead>
                <tbody>${items}</tbody>
                <tfoot>
                    <tr><td colspan="3" style="padding:6px;text-align:right"><strong>TOTAL</strong></td>
                        <td style="padding:6px;text-align:right;font-family:monospace"><strong>${(q.total || 0).toFixed(2)} €</strong></td></tr>
                </tfoot>
            </table>
            ${q.notes ? `<p style="font-size:12px;margin-top:12px;color:#5A6A62"><strong>Notas:</strong> ${q.notes.replace(/</g, "&lt;")}</p>` : ""}
            <p style="font-size:12px;margin-top:16px;color:#5A6A62">Al firmar, acepto el presupuesto arriba detallado. La clínica emitirá la factura oficial VeriFactu correspondiente.</p>
        </div>
    `;
}
